import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useLogin } from "@/lib/api";
import { logger } from "@/lib/logger";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Vogsy-BOI-mcp-test";

export function LoginPage() {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    logger.debug("[login] submitting for", email);
    try {
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      logger.debug("[login] success → redirecting to /home", result);
      navigate("/home");
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      if (status === 403 && detail === "email_not_verified") {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }
      logger.debug("[login] failed:", err);
      setError(status === 401 ? "Invalid credentials." : "Sign-in failed. Please try again.");
    }
  };

  return (
    <div className="bg-background relative flex min-h-screen overflow-hidden">
      <div className="from-primary via-primary to-foreground relative hidden w-[42%] flex-col justify-between bg-linear-to-b p-10 text-white lg:flex">
        <div className="space-y-6">
          <p className="text-accent-light text-xs font-medium tracking-[0.35em] uppercase">
            Board of Innovation
          </p>
          <div className="space-y-4">
            <h1 className="font-heading max-w-sm text-6xl leading-[0.95] font-normal tracking-[-0.04em]">
              Project signals, surfaced before Slack gets noisy.
            </h1>
            <p className="text-accent-light max-w-md text-sm leading-6">
              A focused internal workspace for MCP-powered reminders, project health checks, and
              status visibility across the BOI team.
            </p>
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div className="space-y-3">
            <p className="text-accent-light text-[11px] tracking-[0.3em] uppercase">
              Editorial systems for internal ops
            </p>
            <div className="flex gap-2">
              <span className="bg-accent h-16 w-3" />
              <span className="bg-accent/80 h-16 w-3" />
              <span className="bg-accent/55 h-16 w-3" />
              <span className="bg-accent/30 h-16 w-3" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8">
        <Card className="w-full max-w-md border-white/60 bg-white/90 shadow-[0_24px_80px_rgba(13,19,20,0.08)] backdrop-blur">
          <CardHeader className="space-y-4 text-left">
            <div className="space-y-2">
              <p className="text-muted text-xs font-medium tracking-[0.35em] uppercase">
                Vogsy MCP Connector
              </p>
              <CardTitle className="font-heading text-primary text-4xl leading-none font-normal tracking-[-0.04em]">
                {PROJECT_NAME}
              </CardTitle>
            </div>
            <p className="text-muted max-w-sm text-sm leading-6">
              Sign in to review reminder flows, check project status, and keep internal delivery
              rituals moving.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-foreground text-xs font-medium tracking-[0.22em] uppercase"
                >
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@boardofinnovation.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="text-foreground text-xs font-medium tracking-[0.22em] uppercase"
                >
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked === true)}
                />
                <label htmlFor="rememberMe" className="text-foreground text-sm">
                  Remember me for 30 days
                </label>
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <Button
                type="submit"
                disabled={loginMutation.isPending}
                className="bg-accent text-accent-foreground hover:bg-accent/90 w-full"
              >
                {loginMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
              <p className="text-muted text-center text-sm">
                Don&apos;t have an account?{" "}
                <Link to="/signup" className="text-accent hover:underline">
                  Sign up
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
